import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import { Card, CardContent, CardDescription, CardTitle } from '@/components/ui-kit/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import { ScrollArea } from '@/components/ui-kit/scroll-area';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { TablePagination } from '@/components/shared/TablePagination';
import { TEMPLATE_CATEGORIES } from '@/modules/templates/constants/template-categories';
import { getTemplateIcon } from '@/modules/templates/constants/template-icon-map';
import { useGetAvailableTemplatesForOrgPaginated } from '@/modules/templates/hooks/use-templates';
import type { Template } from '@/modules/templates/types/templates.types';
import { getCreateHref } from '@/modules/templates/utils/template-href';
import { useCurrentOrgId } from '@/store/use-my-oganization-store';
import { AddSquareIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowUpRight, FileSearch, FileText, LockKeyhole, Search, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  hasActiveDocumentGenerationSubscription,
  hasExhaustedDocumentGenerationQuota,
  useActiveSubscription,
} from '@/modules/subscription/hooks/use-active-subscription';

const PAGE_SIZE = 9;

const NewDocumentDialog = ({ className }: { className?: string }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);
  const orgId = useCurrentOrgId();
  const { data: subscription, isSuccess: isSubscriptionLoaded } = useActiveSubscription();
  const hasActiveSubscription = hasActiveDocumentGenerationSubscription(subscription);
  const hasExhaustedQuota = hasExhaustedDocumentGenerationQuota(subscription);
  const showNoActiveSubscription = isSubscriptionLoaded && !hasActiveSubscription;
  const showQuotaExhausted =
    isSubscriptionLoaded && hasActiveSubscription && hasExhaustedQuota;
  // Fail closed while the subscription is unresolved as well as when it is
  // confirmed missing. Navigation/filtering remains available.
  const isTemplateCreationDisabled =
    !isSubscriptionLoaded || !hasActiveSubscription || hasExhaustedQuota;
  const subscriptionWarning = showQuotaExhausted
    ? t('SUBSCRIPTION_QUOTA_EXHAUSTED')
    : showNoActiveSubscription
      ? t('SUBSCRIPTION_NO_ACTIVE')
      : null;

  // Debounced search — same pattern as admin templates page
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 400);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleCategoryChange = (slug: string | null) => {
    setSelectedCategory(slug);
    setPage(1);
  };

  // Server-side paginated query via UDS
  const { data: result, isLoading } = useGetAvailableTemplatesForOrgPaginated({
    orgId,
    pageNo: page,
    pageSize: PAGE_SIZE,
    search: debouncedSearch,
    category: selectedCategory,
  });

  const templates: Template[] = result?.items || [];
  const totalCount: number = result?.totalCount || 0;
  const totalPages: number = result?.totalPages || 1;

  const selectedCategoryLabel = selectedCategory
    ? TEMPLATE_CATEGORIES.find((c) => c.slug === selectedCategory)?.label
    : null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="primary"
          className={cn('md:w-full font-heading flex items-center justify-center gap-2', className)}
          size="sm"
        >
          <HugeiconsIcon icon={AddSquareIcon} size={28} />
          <span>{t('ORGSIDEBAR_CREATEDOCUMENT')}</span>
        </Button>
      </DialogTrigger>
      <DialogContent
        hideClose
        className="w-screen h-dvh max-w-full rounded-none  sm:h-[95vh] sm:w-[95vw] sm:rounded-3xl lg:max-w-6xl p-0 sm:p-0 gap-0 overflow-hidden bg-white border-0 shadow-2xl flex! flex-col md:flex-row focus:outline-none"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{t('ORGSIDEBAR_CREATEDOCUMENT')}</DialogTitle>
          <DialogDescription>Select a template to create a new document.</DialogDescription>
        </DialogHeader>

        {/* Mobile-only top bar — title + close (desktop's title bar lives in main content) */}
        <div className="md:hidden flex items-center justify-between gap-2 h-14 px-4 border-b border-border/40 bg-white shrink-0">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-foreground truncate">
              {selectedCategoryLabel ?? t('ALL_CONTRACTS')}
            </h2>
            {subscriptionWarning ? (
              <p className="text-xs font-medium text-amber-700">{subscriptionWarning}</p>
            ) : null}
          </div>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Close"
            className="shrink-0 h-9 w-9 rounded-full hover:bg-muted/50 text-muted-foreground"
            onClick={() => setOpen(false)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Left Sidebar - Categories & Search */}
        <div className="w-full md:w-80 shrink-0 bg-muted/30 border-b md:border-b-0 md:border-r border-border/5 flex flex-col p-3 sm:p-6 gap-3 md:gap-8">
          <div className="space-y-3 sm:space-y-4">
            <div className="relative group">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
              <Input
                placeholder="Search Documents"
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10 bg-white border-border rounded-xl h-11 sm:h-12 focus-visible:ring-0 focus-visible:border-primary transition-all shadow-sm text-sm"
              />
            </div>
          </div>

          {/* Desktop Categories */}
          <div className="flex-col space-y-1 hidden md:flex overflow-y-auto">
            <Button
              variant="ghost"
              onClick={() => handleCategoryChange(null)}
              className={`justify-start gap-3 h-12 rounded-xl px-4 font-semibold ${selectedCategory === null
                  ? 'bg-[hsl(var(--primary)/0.1)] text-primary hover:bg-[hsl(var(--primary)/0.15)] hover:text-primary'
                  : 'text-muted-foreground hover:text-foreground'
                }`}
            >
              <FileText className="h-5 w-5" />
              {t('ALL_CONTRACTS')}
            </Button>
            {TEMPLATE_CATEGORIES.map((cat) => {
              const CatIcon = cat.icon;
              const isActive = selectedCategory === cat.slug;
              return (
                <Button
                  key={cat.slug}
                  variant="ghost"
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`justify-start gap-3 h-12 rounded-xl px-4 font-semibold ${isActive
                      ? 'bg-[hsl(var(--primary)/0.1)] text-primary hover:bg-[hsl(var(--primary)/0.15)] hover:text-primary'
                      : 'text-muted-foreground hover:text-foreground'
                    }`}
                >
                  <CatIcon className="h-5 w-5" />
                  {cat.label}
                </Button>
              );
            })}
          </div>

          {/* Mobile Categories Chips */}
          <div className="flex md:hidden overflow-x-auto gap-2 pb-1 no-scrollbar -mx-2 px-2">
            <Badge
              variant="secondary"
              onClick={() => handleCategoryChange(null)}
              className={`cursor-pointer rounded-full px-4 py-1.5 whitespace-nowrap text-xs font-medium ${selectedCategory === null
                  ? 'bg-primary text-white hover:bg-primary'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
            >
              {t('ALL_CONTRACTS')}
            </Badge>
            {TEMPLATE_CATEGORIES.map((cat) => (
              <Badge
                key={cat.slug}
                variant="secondary"
                onClick={() => handleCategoryChange(cat.slug)}
                className={`cursor-pointer rounded-full px-4 py-1.5 whitespace-nowrap text-xs font-medium ${selectedCategory === cat.slug
                    ? 'bg-primary text-white hover:bg-primary'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
              >
                {cat.label}
              </Badge>
            ))}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 min-w-0 min-h-0 bg-white flex flex-col relative overflow-hidden">
          <div className="px-8 py-4 hidden md:flex items-center justify-between border-b border-[hsl(var(--border)/0.5)] bg-[hsl(var(--white))/0.8] backdrop-blur-md z-10 w-full">
            <div className="flex min-w-0 items-center gap-3">
              <h2 className="shrink-0 text-lg sm:text-2xl font-bold text-foreground tracking-tight">
                {selectedCategoryLabel ?? t('ALL_CONTRACTS')}
              </h2>
              {subscriptionWarning ? (
                <p className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-sm font-medium text-amber-700">
                  {subscriptionWarning}
                </p>
              ) : null}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full hover:bg-[hsl(var(--muted)/0.5)] text-muted-foreground"
              onClick={() => setOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          <div className="flex-1 min-h-0 relative">
            <ScrollArea className="absolute inset-0 h-full w-full">
              <div className="p-4 sm:p-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 pb-12 sm:pb-8">
                {isLoading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <Card
                      key={i}
                      className="border border-[hsl(var(--border)/0.5)] shadow-sm rounded-2xl flex flex-col h-full bg-[hsl(var(--muted)/0.2)]"
                    >
                      <CardContent className="p-4 space-y-4 flex flex-col flex-1">
                        <Skeleton className="h-11 w-11 sm:h-12 sm:w-12 rounded-xl" />
                        <div className="space-y-2 flex-1">
                          <Skeleton className="h-5 w-3/4" />
                          <Skeleton className="h-4 w-1/2" />
                        </div>
                        <Skeleton className="h-10 w-full sm:w-32 rounded-xl" />
                      </CardContent>
                    </Card>
                  ))
                ) : templates.length === 0 ? (
                  <div className="col-span-full flex flex-col items-center justify-center py-20 px-4 text-center space-y-4">
                    <div className="bg-[hsl(var(--muted)/0.3)] p-6 rounded-full">
                      <FileSearch className="h-12 w-12 text-muted-foreground" />
                    </div>
                    <div className="space-y-1">
                      <h3 className="text-xl font-bold text-foreground">No templates found</h3>
                      <p className="text-muted-foreground max-w-70">
                        {debouncedSearch
                          ? `We couldn't find any templates matching "${debouncedSearch}". Try another keyword.`
                          : 'No templates available in this category.'}
                      </p>
                    </div>
                    {(debouncedSearch || selectedCategory) && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          setSearch('');
                          setDebouncedSearch('');
                          setSelectedCategory(null);
                          setPage(1);
                        }}
                        className="rounded-xl"
                      >
                        Clear Filters
                      </Button>
                    )}
                  </div>
                ) : (
                  templates.map((template) => {
                    const Icon = getTemplateIcon(template.icon);
                    const categoryInfo = TEMPLATE_CATEGORIES.find(
                      (c) => c.slug === template.category
                    );
                    const CategoryIcon = categoryInfo?.icon;
                    const createHref = getCreateHref(template);
                    return (
                      <Card
                        key={template.id}
                        aria-disabled={isTemplateCreationDisabled}
                        className={cn(
                          'group relative border border-[hsl(var(--border)/0.5)] shadow-sm transition-all duration-300 rounded-2xl flex flex-col h-full bg-[hsl(var(--muted)/0.2)] overflow-hidden',
                          isTemplateCreationDisabled
                            ? 'cursor-not-allowed'
                            : 'hover:shadow-xl hover:shadow-[hsl(var(--primary)/0.05)] hover:-translate-y-1'
                        )}
                      >
                        {isTemplateCreationDisabled ? (
                          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-grayscale-35">
                            <span className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-white/90 text-muted-foreground shadow-sm">
                              <LockKeyhole className="h-4 w-4" aria-hidden="true" />
                            </span>
                          </div>
                        ) : null}
                        <CardContent className="px-4 space-y-4 flex flex-col flex-1">
                          <div className="flex items-center justify-between">
                            <div className="flex h-11 w-11 items-center justify-center rounded-md bg-selise-blue-500/10 border border-[hsl(var(--border)/0.5)] shadow-sm text-primary group-hover:bg-primary group-hover:text-white group-hover:border-primary transition-all duration-300">
                              <Icon className="h-5 w-5" />
                            </div>
                            {categoryInfo ? (
                              <Badge className="w-fit bg-selise-blue/12 text-selise-blue font-subheading uppercase tracking-[0.12em] gap-1">
                                {CategoryIcon && <CategoryIcon className="h-3 w-3" />}
                                {categoryInfo.label}
                              </Badge>
                            ) : (
                              <Badge className="w-fit bg-selise-blue/12 text-selise-blue font-subheading uppercase tracking-[0.12em]">
                                {template.popular
                                  ? t('TEMPLATES_MOSTPOPULAR')
                                  : t('TEMPLATES_LIVE')}
                              </Badge>
                            )}
                          </div>
                          <div className="space-y-1.5 flex-1">
                            <CardTitle className="text-base font-semibold text-primary-light line-clamp-2">
                              {template.title}
                            </CardTitle>
                            <CardDescription className="text-sm leading-relaxed text-muted-foreground line-clamp-2">
                              {template.description}
                            </CardDescription>
                          </div>
                          {isTemplateCreationDisabled ? (
                            <Button size="sm" variant="primary" disabled>
                              Create Document
                              <ArrowUpRight className="ml-2 h-4 w-4" />
                            </Button>
                          ) : (
                            <Button size="sm" asChild variant="primary">
                              <Link to={createHref}>
                                Create Document
                                <ArrowUpRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
                              </Link>
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 sm:px-8 py-3 border-t border-[hsl(var(--border)/0.5)] bg-white">
              <TablePagination
                pagination={{
                  page,
                  limit: PAGE_SIZE,
                  total: totalCount,
                  totalPages,
                }}
                onPageChange={setPage}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewDocumentDialog;
